import { useEffect, useMemo, useState } from 'react'

import { useAccount } from 'wagmi'
import { backendApi } from 'utils/backend-api'

import useContracts from 'hooks/mint/useContracts'
import {
  GetNftsBalanceFromAlchemyResponse,
  useNftsInWalletByCollection,
} from 'hooks/nfts/useNftsInWallet'

import { DEFAULT_CHAIN_ID } from 'config'
import { inferQueryOutput, trpc } from '~/utils/trpc'
import { find } from 'lodash'
import { SWRResponse } from 'swr'

export interface BasePageInfo {
  id: string
}

export interface GlaminatedPageInfo extends BasePageInfo, inferQueryOutput<'page.byIds'> {}

export type OwnedPageInfo = BasePageInfo | GlaminatedPageInfo

function usePagesOwned(): [
  (OwnedPageInfo | { id: string })[] | undefined,
  boolean,
  SWRResponse<GetNftsBalanceFromAlchemyResponse | null, any>,
  any,
] {
  const chainId = DEFAULT_CHAIN_ID

  const [_gobblers, _goo, pageContract] = useContracts(chainId)
  const { address } = useAccount()
  const pagesOwnedQuery = useNftsInWalletByCollection(address, pageContract?.address, chainId)

  const pagesOwnedByUser = pagesOwnedQuery.data

  const pageIdsOwned = useMemo(() => {
    if (!pagesOwnedByUser || !pageContract) {
      return
    }
    const rawPages = pagesOwnedByUser.ownedNfts
    const pageIds = rawPages.map((page) => parseInt(page.id.tokenId).toString())
    return pageIds
  }, [pageContract, pagesOwnedByUser])

  const glaminatedOwnedPagesQuery = trpc.useQuery(
    [
      'page.byIds',
      {
        chainId: DEFAULT_CHAIN_ID,
        pageIds: pageIdsOwned!,
      },
    ],
    { enabled: !!pageIdsOwned },
  )

  console.log('glaminatedOwnedPagesQuery:', glaminatedOwnedPagesQuery)

  const isLoading =
    (pagesOwnedQuery.isValidating && !pagesOwnedQuery.data) || glaminatedOwnedPagesQuery.isLoading

  const glamiantedPages = glaminatedOwnedPagesQuery.data
  const pagesWithData: (OwnedPageInfo | { id: string })[] | undefined = useMemo(
    () =>
      pageIdsOwned?.map((pageId) => {
        const maybeGlaminatedPageData = find(glamiantedPages, (p) => p.id === pageId)
        if (maybeGlaminatedPageData) {
          return maybeGlaminatedPageData
        }
        return {
          id: pageId,
        }
      }),
    [glamiantedPages, pageIdsOwned],
  )

  return [pagesWithData, isLoading, pagesOwnedQuery, glaminatedOwnedPagesQuery]
}

export { usePagesOwned }
